/**
 * The one place that decides what an EMPTY calendar period is allowed to say.
 *
 * An empty grid means three very different things, and saying the wrong one is what made
 * the filters look broken:
 *   - nothing is scheduled in this period, full stop;
 *   - the filters are hiding what there is;
 *   - nothing is LEFT to send — what was here already went out, and "Show sent" is off.
 *     Under the default filters that is exactly what a finished month looks like, so the
 *     empty period offers "Show sent" (without counting what it would show — see below).
 *
 * The toolbar and the centred month caption answer the same question in two places and
 * must never be able to disagree, so neither owns the decision: both ask
 * `emptyPeriodCopy`. Each view points at its own keys rather than gluing a period phrase
 * onto a number, because no other language would accept that construction.
 */
import type { CalendarGridView } from './types';

/** "9 sends this month" — the populated case. */
export const COUNT_KEY: Record<CalendarGridView, string> = {
    month: 'calendar.grid.sendsMonth',
    week: 'calendar.grid.sendsWeek',
    day: 'calendar.grid.sendsDay',
};

/** Genuinely nothing scheduled in this period. */
export const EMPTY_KEY: Record<CalendarGridView, string> = {
    month: 'calendar.grid.nothingMonth',
    week: 'calendar.grid.nothingWeek',
    day: 'calendar.grid.nothingDay',
};

/**
 * There ARE sends in this period; a filter is what emptied the grid.
 * "Nothing scheduled this month", shown next to a sidebar full of failed sends,
 * is a lie — and it is the specific lie that made the filters look broken.
 */
export const EMPTY_FILTERED_KEY: Record<CalendarGridView, string> = {
    month: 'calendar.grid.nothingMonthFiltered',
    week: 'calendar.grid.nothingWeekFiltered',
    day: 'calendar.grid.nothingDayFiltered',
};

/** The action that reveals what already went out — the same words as the switch it flips. */
export const SHOW_SENT_KEY = 'calendarSidebarFilters.showSent.title';

export type EmptyPeriodCopy =
    | { kind: 'nothing'; textKey: string; actionKey?: string }
    | { kind: 'filtered'; textKey: string };

/**
 * What an empty period says.
 *
 * @param narrowed a filter is hiding something the DEFAULT would show (`isNarrowed`) —
 *                 a kind off, failures off, or "Groups only". NOT "Show sent" off: that is
 *                 the default.
 * @param showSent whether sends that already went out are being shown.
 *
 * A narrowing filter is checked first, because then "nothing scheduled" may be false (the
 * hidden kind could hold upcoming sends), whereas "your filters are hiding everything" is
 * true either way. Otherwise nothing is scheduled — true whatever already went out — and,
 * while "Show sent" is off, the one way to see what did go out is offered beside it.
 *
 * Deliberately WITHOUT a count of what went out: counting it would mean fetching sent
 * sends the user has not asked to see, and the product owner's rule is that nothing sent
 * is fetched until they do.
 */
export function emptyPeriodCopy(
    view: CalendarGridView,
    { narrowed, showSent }: { narrowed: boolean; showSent: boolean },
): EmptyPeriodCopy {
    if (narrowed) return { kind: 'filtered', textKey: EMPTY_FILTERED_KEY[view] };
    return showSent ? { kind: 'nothing', textKey: EMPTY_KEY[view] } : { kind: 'nothing', textKey: EMPTY_KEY[view], actionKey: SHOW_SENT_KEY };
}

/** Translate a copy choice. */
export const emptyPeriodText = (t: (key: string, options?: Record<string, unknown>) => string, copy: EmptyPeriodCopy): string =>
    t(copy.textKey);
